// SourceWizardContext — mounts one `ManagedSourceWizard` in the authenticated
// shell (sibling of `AssistantProvider`) and exposes an imperative opener that
// resolves with the created source. Lets both the assistant drawer and the data
// chat start the "add a data source" flow and get real completion feedback back
// into the chat card (plan 2026-07-29 assistant-connect-source, step 4).
//
// Contract of `openSourceWizard(connectorType, opts?)`:
//   - opens the wizard pre-selected for `connectorType`, in the managed mode
//     unless `opts.transformOnly` says otherwise,
//   - resolves `{ dataSourceId, name }` when the source is created,
//   - resolves `null` when the user cancels / closes without creating.
//
// Every open writes the FULL request (connector + mode + a fresh wizard key) —
// nothing is inherited from a previous open. See the reset comments below for
// what each of those prevents.
//
// Only one wizard runs at a time. If `openSourceWizard` is called while a
// wizard is already open, the in-flight promise is settled with `null` (treated
// as a cancel of the earlier request) and the new request replaces it — the
// simplest single-instance policy, and it matches the user intent of "open the
// most recent thing I asked for".
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ManagedSourceWizard } from "@/components/managed-sources/ManagedSourceWizard";
import type { ManagedSource } from "@/types/managedSources";
import {
  emitSourceCreated,
  setSourceWizardOpener,
  type OpenSourceWizard,
  type SourceWizardResult,
} from "@/context/sourceWizardBridge";

interface SourceWizardContextValue {
  /** Open the wizard pre-selected for `connectorType`; resolves with the
   *  created source or `null` on cancel. Shares its type with the module-level
   *  bridge so the hook and the imperative entry point can never drift. */
  openSourceWizard: OpenSourceWizard;
}

const SourceWizardContext = createContext<SourceWizardContextValue>({
  openSourceWizard: () =>
    Promise.reject(new Error("SourceWizardProvider is not mounted.")),
});

type Resolver = (result: SourceWizardResult) => void;

export function SourceWizardProvider({ children }: { children: ReactNode }) {
  const [opened, setOpened] = useState(false);
  const [connectorType, setConnectorType] = useState<string | null>(null);
  const [transformOnly, setTransformOnly] = useState(false);

  // Bumped on every open and used as the wizard's `key`, so React throws the
  // previous instance away and mounts a fresh one.
  //
  // Why a key and not the wizard's own reset: `ManagedSourceWizard` resets its
  // internal state in an effect keyed on `[opened]`. On the replace-in-flight
  // path (open called while a wizard is already open) `opened` stays `true`,
  // the dependency never changes, and that reset simply does not run — the
  // previous source's connector, auth and category selections would stay
  // mounted under the new connector. Remounting resets ALL of its state,
  // including the fields that effect happens to miss today.
  const [wizardInstance, setWizardInstance] = useState(0);

  // The pending promise's resolver. Held in a ref (not state) so the wizard's
  // stable callbacks can settle it without re-subscribing. Nulled the instant
  // it's consumed — that null-check is what prevents a double-resolve when
  // `onCreated` (success) is followed by `onClose` (the user closing the
  // success screen).
  const resolverRef = useRef<Resolver | null>(null);

  const settle = useCallback((result: SourceWizardResult) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(result);
  }, []);

  const openSourceWizard = useCallback<OpenSourceWizard>(
    (connector, opts) =>
      new Promise<SourceWizardResult>((resolve) => {
        // A wizard is already open: cancel the earlier request, then replace it.
        settle(null);
        resolverRef.current = resolve;
        setConnectorType(connector);
        // Written on EVERY open, never inherited. A caller that omits `opts`
        // (the assistant handoff) means "managed"; a leftover `true` from an
        // earlier transform-only open would make the wizard submit
        // `ingest_mode: "TRANSFORM_ONLY"`, silently creating a source of the
        // wrong tier with no ingestion at all.
        setTransformOnly(opts?.transformOnly ?? false);
        setWizardInstance((n) => n + 1);
        setOpened(true);
      }),
    [settle],
  );

  // Success: resolve with the created source. The modal stays open on its
  // success screen; the user closes it afterwards (onClose finds the resolver
  // already consumed, so it won't overwrite the result with null).
  const handleCreated = useCallback(
    (source: ManagedSource) => {
      const result = {
        dataSourceId: source.data_source_id,
        name: source.data_source_name,
      };
      settle(result);
      // Notify any open Sources page to refetch (this wizard instance is not
      // the one that page owns, so its in-page onCreated won't fire).
      emitSourceCreated(result);
    },
    [settle],
  );

  // Close: resolve `null` if we get here without a create (cancel). If a create
  // already resolved the promise, `settle` is a no-op.
  const handleClose = useCallback(() => {
    setOpened(false);
    setConnectorType(null);
    // Back to the default mode while closed, so nothing can read a stale
    // transform-only flag before the next open writes its own value.
    setTransformOnly(false);
    settle(null);
  }, [settle]);

  // Publish the imperative opener to the module-level bridge so non-React
  // callers (builtinActions.ts) can reach it. Cleared on unmount.
  useEffect(() => {
    setSourceWizardOpener(openSourceWizard);
    return () => {
      setSourceWizardOpener(null);
      // Provider tearing down mid-flow (route/flag change): don't leave a
      // caller hanging forever — settle any pending promise as a cancel.
      settle(null);
    };
  }, [openSourceWizard, settle]);

  const value = useMemo<SourceWizardContextValue>(
    () => ({ openSourceWizard }),
    [openSourceWizard],
  );

  return (
    <SourceWizardContext.Provider value={value}>
      {children}
      <ManagedSourceWizard
        // Fresh instance per open — see `wizardInstance` above.
        key={wizardInstance}
        opened={opened}
        onClose={handleClose}
        onCreated={handleCreated}
        initialConnectorType={connectorType}
        // Managed unless the opener asked for transform-only; the assistant
        // handoff passes no options and therefore always gets managed.
        transformOnly={transformOnly}
        // Above the assistant drawer (both default to Mantine's modal z-index
        // 200); 300 stays below notifications (400) so toasts still surface.
        zIndex={300}
      />
    </SourceWizardContext.Provider>
  );
}

export function useSourceWizard() {
  return useContext(SourceWizardContext);
}
